import * as Parsimmon from 'parsimmon';
import { compose, flatten, of, splitAt } from 'ramda';

type DynamicSegment = {
  kind: 'DynamicSegment';
  values: Val[];
};

type Num = {
  kind: 'Num';
  value: number;
};

type Lit = {
  kind: 'Lit';
  value: string;
};

type Op = {
  kind: 'Op';
  op: string;
};

type Lookup = {
  kind: 'Lookup';
  lookupKey: string;
};

type Val = Num | Lit | Op | Lookup;
type Expr = DynamicSegment | Val;
type Content = Expr[];

const dynamicSegmentG = (x): DynamicSegment => ({
  kind: 'DynamicSegment',
  values: x,
});
const numG = (x): Num => ({ kind: 'Num', value: x });
const litG = (x): Lit => ({ kind: 'Lit', value: x });
const opG = (x): Op => ({ kind: 'Op', op: x });
const lookupG = (x): Lookup => ({ kind: 'Lookup', lookupKey: x });

const dynamicSegmentStart = '##(';

/*
  This is a quick hack as the Content ruleset doesn't have backtracking.
  Should've actually been handled by the parser.
*/
const cleanupContent = parseResult => {
  return parseResult.reduce(
    ({ final, buffer: bufferAcc }, next, idx) => {
      const flushBuffer = bufferArg =>
        bufferArg.length > 0 ? [...final, litG(bufferArg.join(''))] : final;

      if (next['kind']) {
        return { final: [...flushBuffer(bufferAcc), next], buffer: [] };
      } else if (idx === parseResult.length - 1) {
        return { final: flushBuffer([...bufferAcc, next]), buffer: [] };
      } else {
        return { final, buffer: [...bufferAcc, next] };
      }
    },
    { final: [], buffer: [] },
  );
};

export function* getCMSContentHandler(input: string) {
  const expressions = processCMSContentString(input);

  const lookupTable: LookupTable = yield gatherLookupDependencies(expressions);

  const result = compose(
    reifyExpressions,
    evaluateExpressions,
    resolveLookups(lookupTable),
  )(expressions);

  return result;
}

function createParserInstance() {
  return Parsimmon.createLanguage({
    Content: r =>
      Parsimmon.alt(r.DynamicSegment, Parsimmon.any).many().map(cleanupContent),
    DynamicSegment: r =>
      r.Val.trim(Parsimmon.optWhitespace)
        .many()
        .wrap(Parsimmon.string(dynamicSegmentStart), Parsimmon.string(')'))
        .map(dynamicSegmentG),
    Val: r =>
      Parsimmon.alt(r.Num, r.Op, r.Lookup).wrap(
        Parsimmon.string('{'),
        Parsimmon.string('}'),
      ),
    Num: () =>
      Parsimmon.regexp(/#([0-9]+)/, 1)
        .map(Number)
        .map(numG),
    Op: () => Parsimmon.regexp(/%([+-])/, 1).map(opG),
    Lookup: () => Parsimmon.regexp(/\$(\w+)/, 1).map(lookupG),
  });
}

function processCMSContentString(input: string): Content {
  const parser = createParserInstance();
  const parseResult = parser.Content.parse(input);

  if (!parseResult.status) {
    throw new Error('Parser failure.');
  } else {
    const result = parseResult.value.final;

    // This should've also ideally been a part of the parser ruleset.
    result.find((expr: Expr) => {
      if (expr.kind === 'Lit' && expr.value.indexOf(dynamicSegmentStart) > 0) {
        throw new Error('Unparsed dynamic segment.');
      }
    });

    return result;
  }
}

type TraverseCallback = {
  handleDynamicSegment: typeof traverseCommon;
  handleOther: (buffer: Val[], next: Val) => any[];
};

function traverseCommon(
  content: Content,
  { handleDynamicSegment, handleOther }: TraverseCallback,
) {
  return content.reduce((buffer, current) => {
    if (current.kind === 'DynamicSegment') {
      const values = handleDynamicSegment(current.values, {
        handleDynamicSegment,
        handleOther,
      });
      return [...buffer, values];
    } else {
      return handleOther(buffer, current);
    }
  }, []);
}

function gatherLookupDependencies(content: Content): string[] {
  return flatten(
    traverseCommon(content, {
      handleDynamicSegment: gatherLookupDependencies,
      handleOther: (buffer, current) => [...buffer, extractLookupKey(current)],
    }).filter(Boolean),
  );
}

export type LookupTable = { [key: string]: string };

const resolveLookups = (lookupTable: LookupTable) => (content: Content) => {
  return traverseCommon(content, {
    handleDynamicSegment: compose(dynamicSegmentG, resolveLookups(lookupTable)),
    handleOther: (buffer, current) => [
      ...buffer,
      replaceLookupWithValue(lookupTable, current),
    ],
  });
};

function evaluateExpressions(content: Content) {
  return traverseCommon(content, {
    handleDynamicSegment: compose(dynamicSegmentG, evaluateExpressions),
    handleOther: (buffer, current) => tryEvaluateOp([...buffer, current]),
  });
}

function reifyExpressions(content: Content) {
  return flatten(
    traverseCommon(content, {
      handleDynamicSegment: reifyExpressions,
      handleOther: (buffer, current) => [...buffer, reifyVal(current)],
    }),
  ).join('');
}

function extractLookupKey(expr: Expr) {
  if (expr.kind === 'Lookup') {
    return expr.lookupKey;
  } else {
    return null;
  }
}

const replaceLookupWithValue = (
  lookupTable: { [key: string]: string },
  expr: Expr,
) => {
  const resolve = (token: Lookup): Num | Lit => {
    const lookupResult = lookupTable[token.lookupKey];

    if (!lookupResult) {
      throw new Error(`Undefined lookup key : ${token.lookupKey}`);
    }

    const numberValue = Number(lookupResult);
    if (isNaN(numberValue)) {
      return litG(lookupResult);
    } else {
      return numG(numberValue);
    }
  };

  if (expr.kind === 'Lookup') {
    return resolve(expr);
  } else {
    return expr;
  }
};

function tryEvaluateOp(buffer: Val[]) {
  const operations = {
    '-': (a: number, b: number) => a - b,
    '+': (a: number, b: number) => a + b,
  };

  const [rest, lastThree] = splitAt(-3, buffer);
  if (lastThree.length !== 3) {
    return buffer;
  }

  const [arg1, middle, arg2] = lastThree;
  if (middle.kind === 'Op') {
    if (arg1.kind === 'Num' && arg2.kind === 'Num') {
      const operation:
        | undefined
        | (typeof operations)[keyof typeof operations] = operations[middle.op];
      if (operation) {
        const evaluationCall = compose(of, numG, operation);

        return [...rest, ...evaluationCall(arg1.value, arg2.value)];
      }
    } else {
      throw new Error(
        `Faulty operands : ${JSON.stringify(arg1)} , ${JSON.stringify(arg2)}`,
      );
    }
  }

  return buffer;
}

function reifyVal(val: Val) {
  switch (val.kind) {
    case 'Lit':
      return val.value;
    case 'Num':
      return formatNumbers(val.value);
    default:
      throw new Error(
        `Unevaluated operation or lookup token : ${JSON.stringify(val)}`,
      );
  }
}

function formatNumbers(input: number) {
  return new Intl.NumberFormat('en-US').format(input);
}

export const __TEST__ = {
  evaluateExpressions,
  reifyExpressions,
  gatherLookupDependencies,
  getParser: createParserInstance,
  processCMSContentString,
  resolveLookups,
  formatNumbers,
};
